import type {
  BrowserEventPayload,
  BrowserPixelAdapter,
  ScriptErrorReporter,
} from "../BrowserPixelAdapter";
import { injectVendorScript } from "../BrowserPixelAdapter";
import { buildSnapUserData } from "../AdvancedMatching";
import type { MatchIdentity } from "../AdvancedMatching";

export { SnapPixelAdapter, SNAP_PIXEL_ADAPTER_KEY, SNAP_SCRIPT };

const SNAP_PIXEL_ADAPTER_KEY = "snap_pixel";
const SNAP_SCRIPT = "https://sc-static.net/scevent.min.js";

// Lo que el SDK usa del objeto global (stub o scevent.min.js ya cargado):
// una función variádica, `snaptr('init' | 'track', ...)`
type SnaptrLike = ((...args: unknown[]) => void) & {
  queue?: unknown[];
  handleRequest?: (...args: unknown[]) => void;
};

/**
 * Adaptador del Snap Pixel (`snaptr`).
 *
 * Particularidades de la red:
 *
 * - **Instancia compartida**: si `window.snaptr` ya existe (el tenant instaló
 *   el snippet de Snap, p.ej. por GTM) NO se redefine, igual que hace el
 *   snippet oficial; sus píxeles se dejan como están.
 * - **Un solo script** para todos los píxeles (`scevent.min.js`), inyectado
 *   por `injectVendorScript` para que un bloqueo se reporte como en el resto de
 *   redes (`pixel_script_error`).
 * - **El id del píxel en cada `track` siempre**: `snaptr('track', id, NOMBRE,
 *   params)` va solo a ese píxel; `snaptr('track', NOMBRE)` haría broadcast a
 *   todos los píxeles de Snap de la página, también a los ajenos.
 * - **`PAGE_VIEW` no es automático**: lo dispara el mapping `page_view` del
 *   manifest, como un `track` más (el snippet oficial también lo llama a mano).
 * - **Lista cerrada**: los nombres (también los huecos `CUSTOM_EVENT_n`) son de
 *   Snap, así que `event.custom` no cambia la llamada.
 * - **`client_dedup_id`** en los params es el gancho de la dedup con la
 *   Conversions API (mismo píxel + nombre + id, 48 h).
 * - **Advanced matching EN CLARO y síncrono**: `snaptr('init', id,
 *   {user_email, user_phone_number})`; el propio píxel normaliza y hashea
 *   antes de enviar (`u_hem`, `u_hpn`). Como el init es síncrono y fire() lo
 *   llama antes que el track, el SIGN_UP del propio optin ya sale con la
 *   identidad (como Whop, a diferencia de OpenAI y TikTok). Re-llamar a `init`
 *   con el mismo píxel solo fusiona la identidad: uno por identidad y píxel.
 * - **Sin API de consentimiento**: con el consentimiento retirado el adaptador
 *   deja de llamar a `snaptr` (ni init ni track) hasta que vuelva. Lo que se
 *   pide mientras (un píxel nuevo, una identidad nueva) se aplica al volver; los
 *   eventos de ese rato no se mandan.
 */
class SnapPixelAdapter implements BrowserPixelAdapter {
  readonly key = SNAP_PIXEL_ADAPTER_KEY;
  private _loaded = false;
  private _consentGranted = true;
  // Huella de la última identidad enviada por píxel (presente = ya inicializado)
  private _identityApplied: Record<string, string> = {};
  // Init pedidos sin consentimiento, por píxel: se aplican al recuperarlo
  private _pendingInit: Record<string, MatchIdentity | null> = {};
  private _onScriptError: ScriptErrorReporter | null;

  constructor(onScriptError?: ScriptErrorReporter | null) {
    this._onScriptError = onScriptError ?? null;
  }

  load(): void {
    if (this._loaded) {
      return;
    }
    this._loaded = true;
    try {
      if (this._snaptr()) {
        // Ya cargado por el tenant o por GTM: instancia compartida.
        return;
      }
      window.snaptr = createSnaptrStub();
      injectVendorScript(SNAP_SCRIPT, this.key, this._onScriptError);
    } catch {
      // sin píxel de Snap la página sigue funcionando igual
    }
  }

  init(pixelId: string, identity: MatchIdentity | null): void {
    try {
      if (!pixelId) {
        return;
      }
      if (!this._consentGranted) {
        // La última identidad que traiga algo gana; una vacía no borra la anterior
        const previous = this._pendingInit[pixelId];
        const hasIdentity = Object.keys(buildSnapUserData(identity)).length > 0;
        this._pendingInit[pixelId] = hasIdentity || previous === undefined ? identity : previous;
        return;
      }
      const snaptr = this._snaptr();
      if (!snaptr) {
        return;
      }

      const user = buildSnapUserData(identity);
      const fingerprint = JSON.stringify(user);
      const applied = this._identityApplied[pixelId];
      if (applied !== undefined && (applied === fingerprint || Object.keys(user).length === 0)) {
        return;
      }
      this._identityApplied[pixelId] = fingerprint;
      snaptr("init", pixelId, user);
    } catch {
      // un init fallido no puede tumbar el resto de píxeles
    }
  }

  track(pixelId: string, event: BrowserEventPayload, eventId: string): void {
    try {
      if (!this._consentGranted) {
        return;
      }
      const snaptr = this._snaptr();
      if (!snaptr || !pixelId || !event?.name) {
        return;
      }
      snaptr("track", pixelId, event.name, {
        ...(event.params ?? {}),
        client_dedup_id: eventId,
      });
    } catch {
      // el disparo de un píxel jamás afecta al lead ni a la navegación
    }
  }

  setConsent(granted: boolean): void {
    this._consentGranted = granted;
    if (!granted) {
      return;
    }
    const pending = this._pendingInit;
    this._pendingInit = {};
    for (const pixelId of Object.keys(pending)) {
      this.init(pixelId, pending[pixelId]);
    }
  }

  private _snaptr(): SnaptrLike | null {
    const snaptr = window.snaptr;
    return typeof snaptr === "function" ? (snaptr as SnaptrLike) : null;
  }
}

/**
 * Réplica del snippet oficial de Snap (misma forma de cola, que es lo que
 * scevent.min.js drena): una función que delega en `handleRequest` cuando el
 * vendor ya cargó y, mientras tanto, encola sus `arguments` en `queue`. La
 * inyección del script la hace `load()` por `injectVendorScript`.
 */
function createSnaptrStub(): SnaptrLike {
  // Se mantiene `arguments` (y no rest args) porque es lo que el vendor espera
  // encontrar en la cola.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const snaptr: any = function () {
    // eslint-disable-next-line prefer-rest-params
    const args = arguments;
    if (snaptr.handleRequest) {
      // eslint-disable-next-line prefer-spread
      snaptr.handleRequest.apply(snaptr, args);
    } else {
      snaptr.queue.push(args);
    }
  };
  snaptr.queue = [];
  return snaptr as SnaptrLike;
}
